// Thematic grouping for the category navigation (header mega-menu + footer).
// Purely presentational — it does not affect the symbol data or the build.
// Every category should appear in exactly one group; any category not listed
// here falls into a trailing "More" group so nothing can disappear from the
// menus when a new category is added.
import { categories, type SymbolCategory } from "./symbols";

export interface CategoryGroup {
  title: string;
  slugs: string[];
}

export const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    title: "Arrows & Shapes",
    slugs: [
      "arrow-symbols",
      "line-symbols",
      "box-drawing-symbols",
      "geometric-shape-symbols",
      "circle-symbols",
      "square-symbols",
      "triangle-symbols",
      "bullet-symbols",
    ],
  },
  {
    title: "Math & Numbers",
    slugs: [
      "math-symbols",
      "number-symbols",
      "superscript-subscript-symbols",
      "fraction-symbols",
      "currency-symbols",
    ],
  },
  {
    title: "Punctuation & Brackets",
    slugs: ["check-cross-symbols", "bracket-symbols", "quote-symbols", "punctuation-symbols"],
  },
  {
    title: "Tech & Signs",
    slugs: [
      "keyboard-symbols",
      "technical-symbols",
      "office-copyright-trademark-symbols",
      "warning-symbols",
      "medical-symbols",
      "transport-map-symbols",
    ],
  },
  {
    title: "Culture & Beliefs",
    slugs: [
      "zodiac-symbols",
      "planet-symbols",
      "religious-symbols",
      "gender-symbols",
      "chess-symbols",
      "card-dice-symbols",
      "music-symbols",
    ],
  },
  {
    title: "Hearts & Nature",
    slugs: ["heart-symbols", "star-symbols", "flower-symbols", "nature-symbols", "animal-symbols", "weather-symbols", "people-hands"],
  },
];

export interface ResolvedCategoryGroup {
  title: string;
  items: SymbolCategory[];
}

/** Resolve the group definitions against the live category list. */
export function getCategoryGroups(): ResolvedCategoryGroup[] {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  const used = new Set<string>();
  const groups: ResolvedCategoryGroup[] = CATEGORY_GROUPS.map((g) => {
    const items = g.slugs.map((s) => bySlug.get(s)).filter((c): c is SymbolCategory => Boolean(c));
    for (const c of items) used.add(c.slug);
    return { title: g.title, items };
  });
  const leftover = categories.filter((c) => !used.has(c.slug));
  if (leftover.length) groups.push({ title: "More", items: leftover });
  return groups;
}

/** Short label for nav menus — drops the trailing "Symbols". */
export function shortCategoryName(name: string): string {
  return name.replace(/\s*Symbols$/, "");
}

const SLUG_TO_GROUP_TITLE = new Map<string, string>();
for (const g of CATEGORY_GROUPS) for (const s of g.slugs) SLUG_TO_GROUP_TITLE.set(s, g.title);

/**
 * Categories related to `slug`, for the "More categories" block on a category
 * page. Returns the OTHER members of the same thematic group (so the block is
 * relevant and differs per page) with a group-aware heading. Falls back to a
 * rotated slice of the catalogue for ungrouped/tiny groups so it still varies.
 */
export function getRelatedCategories(slug: string): { heading: string; items: SymbolCategory[] } {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  const groupTitle = SLUG_TO_GROUP_TITLE.get(slug);
  if (groupTitle) {
    const group = CATEGORY_GROUPS.find((g) => g.title === groupTitle);
    const items = (group?.slugs ?? [])
      .filter((s) => s !== slug)
      .map((s) => bySlug.get(s))
      .filter((c): c is SymbolCategory => Boolean(c));
    if (items.length >= 2) return { heading: `More ${groupTitle}`, items };
  }
  // Ungrouped or a group too small to fill a row: rotate the catalogue so each
  // page shows a different set rather than the same first six everywhere.
  const start = Math.max(0, categories.findIndex((c) => c.slug === slug));
  const rotated = [...categories.slice(start + 1), ...categories.slice(0, start)];
  return { heading: "More categories", items: rotated.filter((c) => c.slug !== slug).slice(0, 6) };
}

// A small curated set surfaced in the footer (the full set lives in the header
// mega-menu and on /categories). Editorial selection — not a popularity claim.
export const FOOTER_CATEGORY_SLUGS: string[] = [
  "heart-symbols",
  "star-symbols",
  "arrow-symbols",
  "check-cross-symbols",
  "math-symbols",
  "currency-symbols",
  "bullet-symbols",
  "music-symbols",
];

export function getFooterCategories(): SymbolCategory[] {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  return FOOTER_CATEGORY_SLUGS.map((s) => bySlug.get(s)).filter((c): c is SymbolCategory => Boolean(c));
}
